"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import { useSearchParams } from "next/navigation"

import { pathWithQuery } from "@/lib/routes"
import { queryToUrlParams, urlParamsToQuery } from "@/lib/services/search/search"
import { DEFAULT_SEARCH_QUERY } from "@/lib/types/search"
import type { SearchQuery } from "@/lib/types/search"

interface UseSearchQueryOptions {
  baseQuery?: Partial<SearchQuery>
}

interface UseSearchQueryResult {
  query: SearchQuery
  params: string
  updateQuery: (updates: Partial<SearchQuery>) => void
}

interface PushedQuery {
  params: string
  query: SearchQuery
}

const addressParams = (): string => new URLSearchParams(window.location.search).toString()

export const useSearchQuery = (options: UseSearchQueryOptions = {}): UseSearchQueryResult => {
  const { baseQuery = {} } = options
  const searchParams = useSearchParams()
  const [pushed, setPushed] = useState<PushedQuery | null>(null)

  const mergedBase = useMemo(() => ({ ...DEFAULT_SEARCH_QUERY, ...baseQuery }), [baseQuery])
  const paramString = searchParams.toString()

  useEffect(() => {
    setPushed((current) => (current?.params === addressParams() ? current : null))
  }, [paramString])

  const urlQuery = useMemo(() => urlParamsToQuery(searchParams, mergedBase), [searchParams, mergedBase])

  const updateQuery = useCallback(
    (updates: Partial<SearchQuery>) => {
      const nextQuery = { ...urlParamsToQuery(new URLSearchParams(window.location.search), mergedBase), ...updates }
      const params = queryToUrlParams(nextQuery, mergedBase).toString()
      window.history.pushState(null, "", pathWithQuery(window.location.pathname, params))
      setPushed({ params, query: urlParamsToQuery(new URLSearchParams(params), mergedBase) })
    },
    [mergedBase]
  )

  return { query: pushed?.query ?? urlQuery, params: pushed?.params ?? paramString, updateQuery }
}
